import { nbVal, nbValOrUndef } from "@paroi/data-formatters-lib";
import { useWrapAsync } from "@paroicms/front-app-log";
import { Button } from "primereact/button";
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  deleteReference,
  generateReferenceDatabase,
  publishReference,
} from "../../../../api/application/reference";
import { apiRequestWrapper } from "../../../../api/shared/http-client";
import { useAppStore } from "../../../../store/app.store";
import useGetReference from "../../../hooks/useGetReference";
import useGetReferences from "../../../hooks/useGetReferences";
import useGetTableForm from "../../../hooks/useGetTableForm";
import useGetTables from "../../../hooks/useGetTables";
import { syncOpenModalDialog } from "../../../shared/global-tools/dialog-system";
import { waitFor } from "../../../shared/wait-for";
import makeConfirmDialog from "../../utils/ConfirmDialog/ConfirmDialog";
import Icon from "../../utils/Icons/Icon";
import OurBadge from "../../utils/OurBadge/OurBadge";
import OurDataTable, { type ColumnType } from "../../utils/OurDataTable/OurDataTable";
import OurDropdown from "../../utils/OurDropdown/OurDropdown";
import Spinner from "../../utils/Spinner/Spinner";
import SwitchBadge from "../../utils/SwitchBadge/SwitchBadge";
import "./index.scss";

export default function DatabaseViewPage() {
  const { databaseId, tableId } = useParams();
  const databaseIdParam = nbVal(databaseId);
  const tableIdParam = nbValOrUndef(tableId);
  const [selectedTableId, setSelectedTableId] = useState<number>();
  const [selectedDatabaseId, setSelectedDatabaseId] = useState<number>();
  const { references: databases, searchReferences: searchDatabases } = useGetReferences();
  const {
    reference: selectedReference,
    fetchReference,
    loadingReference,
  } = useGetReference({
    referenceId: selectedDatabaseId,
    preloadData: false,
  });
  const { tables, refreshTables, filterTables } = useGetTables({
    referenceId: selectedDatabaseId,
    preloadData: false,
  });
  const { tableForm, loadingTableForm, fetchTableForm } = useGetTableForm();
  const wrapAsync = useWrapAsync();
  const { setGeneratedReferences, setDatabaseCreationInfo } = useAppStore();
  const navigate = useNavigate();

  const handleSearchDatabases = async (query: string) => {
    await waitFor(500);
    await searchDatabases(query);
    return databases.map((ref) => ({
      value: ref.id,
      label: ref.name,
    }));
  };

  const handleSearchTables = async (query: string) => {
    const filteredTables = filterTables(query);
    return filteredTables.map((table) => ({
      value: table.id,
      label: table.label,
    }));
  };

  const handleDatabaseDeletion = wrapAsync(async () => {
    if (!selectedDatabaseId) return;
    await apiRequestWrapper(async () => {
      await deleteReference(selectedDatabaseId);
      await searchDatabases("");
      setSelectedDatabaseId(undefined);
      setSelectedTableId(undefined);
      setGeneratedReferences(undefined);
    });
  });

  const handlePublishReference = wrapAsync(async () => {
    if (!selectedDatabaseId) return;
    await apiRequestWrapper(async () => {
      await publishReference(selectedDatabaseId);
      await generateReferenceDatabase(selectedDatabaseId);
      await fetchReference(selectedDatabaseId);
      setGeneratedReferences(undefined);
    });
  });

  const handleReferenceEdit = (referenceId: number) => {
    setDatabaseCreationInfo({
      referenceId,
    });
    navigate(`/app/databases/${referenceId}/edit`);
  };

  useEffect(() => {
    if (!selectedDatabaseId) return;
    fetchReference(selectedDatabaseId);
    refreshTables(selectedDatabaseId);
  }, [selectedDatabaseId]);

  useEffect(() => {
    if (!selectedDatabaseId || !selectedTableId) return;
    fetchTableForm({ referenceId: selectedDatabaseId, tableId: selectedTableId });
  }, [selectedDatabaseId, selectedTableId]);

  useEffect(() => {
    if (databaseIdParam) setSelectedDatabaseId(databaseIdParam);
  }, [databaseIdParam]);

  useEffect(() => {
    if (tableIdParam) setSelectedTableId(tableIdParam);
  }, [tableIdParam]);

  const displayColumns = useMemo(() => {
    if (tableForm && tableForm.length > 0) {
      return tableForm.map((field) => {
        return {
          label: field.label,
          type: field.data_source?.type ? `${field.type}-${field.data_source.type}` : field.type,
          length: field.length || null,
          nullable: !field.required,
          required: field.required,
          unique: field.unique,
          hasRelation: !!field.data_source,
        };
      });
    }
    return [];
  }, [tableForm]);

  const getColumnTypeStatus = (type: string): "success" | "error" | "info" => {
    if (type === "varchar" || type === "text" || type === "string") return "success";
    if (type === "int") return "info";
    if (type === "date" || type === "timestamp") return "error";
    return "info";
  };

  const hasRequiredSelections = selectedDatabaseId && selectedTableId;
  const showLoading = loadingTableForm;
  const showEmptyData = displayColumns.length === 0 && hasRequiredSelections;
  const showMissingSelections = !hasRequiredSelections;

  const columns: ColumnType<(typeof displayColumns)[0]>[] = [
    {
      field: "label",
      header: "Libellé du champ",
      style: { minWidth: "150px" },
      body: (datum) => (
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          {datum.hasRelation && <Icon name="key" color="#DE9320" width={15} height={15} />}
          {datum.label}
        </div>
      ),
    },
    {
      field: "type",
      header: "Type du champ",
      style: { minWidth: "120px" },
      body: (datum) => (
        <OurBadge
          status={getColumnTypeStatus(datum.type || "")}
          label={(datum.type || "").toUpperCase()}
        />
      ),
    },
    {
      field: "length",
      header: "Longueur",
      style: { minWidth: "140px" },
      body: (datum) => datum.length || "-",
    },
    {
      field: "unique",
      header: "Unique",
      style: { minWidth: "110px" },
      body: (datum) => <SwitchBadge status={!!datum.unique} />,
    },
    {
      field: "required",
      header: "Est obligatoire ?",
      style: { minWidth: "140px" },
      body: (datum) => <SwitchBadge status={!!datum.required} />,
    },
  ];

  if (loadingReference || !selectedReference) return <Spinner />;
  return (
    <div className="DatabaseViewPage Container">
      <div className="DatabaseViewPage-content">
        <div className="DatabaseViewPage-header">
          <div className="DatabaseViewPage-formRow">
            <label className="Field w-full">
              <span className="Field-label" style={{ fontWeight: 600, fontSize: "15px" }}>
                Référentiels
              </span>
              <OurDropdown<number>
                initialOptions={databases.map((ref) => ({
                  value: ref.id,
                  label: ref.name,
                }))}
                onSearch={handleSearchDatabases}
                filter
                className="sm RelationCard-dropdown"
                selectedOption={selectedDatabaseId}
                onChange={(val) => {
                  setSelectedDatabaseId(val);
                  setSelectedTableId(undefined);
                }}
                placeholder="Sélectionner"
              />
            </label>

            <label className="Field w-full">
              <span className="Field-label" style={{ fontWeight: 600, fontSize: "15px" }}>
                Table
              </span>
              <OurDropdown<number>
                initialOptions={tables.map((table) => ({
                  value: Number(table.id),
                  label: table.label,
                }))}
                onSearch={handleSearchTables}
                filter
                className="sm RelationCard-dropdown"
                selectedOption={selectedTableId}
                onChange={setSelectedTableId}
                placeholder="Sélectionner"
                disabled={!selectedDatabaseId}
              />
            </label>
          </div>

          <div className="DatabaseViewPage-actions">
            {selectedDatabaseId && (
              <Button
                className="Btn blue icon"
                label="Modifier"
                icon={<Icon name="pencil2" color="#fff" />}
                iconPos="left"
                onClick={() => handleReferenceEdit(selectedDatabaseId)}
              />
            )}

            {!selectedReference?.enabled ? (
              <>
                <Button
                  className="Btn success icon"
                  label="Valider"
                  icon={<Icon name="check2" />}
                  iconPos="left"
                  onClick={handlePublishReference}
                />

                <Button
                  className="Btn danger icon"
                  label="Supprimer"
                  icon={<Icon name="trash" />}
                  iconPos="left"
                  onClick={() =>
                    syncOpenModalDialog(makeConfirmDialog, {
                      onConfirm: handleDatabaseDeletion,
                      warningMessage: (
                        <>
                          Voulez-vous supprimer le référentiel{" "}
                          {selectedReference && <b>{selectedReference.name}</b>} ?
                        </>
                      ),
                    })
                  }
                />
              </>
            ) : (
              <>
                <Link
                  to={
                    hasRequiredSelections
                      ? `/app/databases/${selectedDatabaseId}/tables/${selectedTableId}`
                      : "#"
                  }
                  className={`Btn success icon link ${!hasRequiredSelections ? "disabled" : ""}`}
                >
                  <Icon name="database" color="#fff" /> Voir les données
                </Link>

                <Link
                  to={
                    hasRequiredSelections
                      ? `/app/databases/${selectedDatabaseId}/tables/${selectedTableId}/create`
                      : "#"
                  }
                  className={`Btn primary icon link ${!hasRequiredSelections ? "disabled" : ""}`}
                >
                  <Icon name="plus" color="#fff" /> Ajout de données
                </Link>
              </>
            )}
          </div>
        </div>

        <div className="DatabaseViewPage-tableStructure">
          <h3 className="DatabaseViewPage-sectionTitle">
            Structure de la table {!showMissingSelections && `(${displayColumns.length} colonnes)`}
          </h3>

          {showLoading ? (
            <div className="DatabaseViewPage-loading">
              <p>Chargement de la structure de la table...</p>
            </div>
          ) : showEmptyData ? (
            <div className="DatabaseViewPage-empty">
              <p>Aucune colonne trouvée pour cette table.</p>
            </div>
          ) : showMissingSelections ? (
            <div className="DatabaseViewPage-empty">
              <p>Veuillez selectionner une table pour voir la structure des colonnes.</p>
            </div>
          ) : (
            <OurDataTable
              data={displayColumns}
              columns={columns}
              options={{
                stripedRows: true,
                rows: 15,
              }}
              className="DatabaseViewPage-dataTable"
            />
          )}
        </div>
      </div>
    </div>
  );
}
